import { PDFDocument } from "pdf-lib";
import JSZip from "jszip";
import { SendcloudShipment } from "../types/sendcloud";
import { sendcloudService } from "../services/sendcloudService";
import { isOwnDeliveryShipment } from "../constants/ownDelivery";
import {
  generateOwnDeliveryLabelPdf,
  ownDeliveryLabelDataFromShipment,
  resolveOwnDeliveryBookingReference,
  resolveOwnDeliveryInstructions,
} from "./ownDeliveryLabelGenerator";

/**
 * Fetch a single label PDF as a blob
 */
async function fetchLabelAsBlob(
  shipment: SendcloudShipment
): Promise<Blob | null> {
  try {
    if (isOwnDeliveryShipment(shipment)) {
      const labelData = ownDeliveryLabelDataFromShipment(shipment);
      labelData.bookingReference = await resolveOwnDeliveryBookingReference(
        shipment.booking_id,
        labelData.bookingReference,
      );
      labelData.instructions = await resolveOwnDeliveryInstructions(
        shipment.booking_id,
        labelData.instructions,
      );
      return generateOwnDeliveryLabelPdf(labelData);
    }

    if (!shipment.sendcloud_shipment_id) {
      return null;
    }

    const result = await sendcloudService.downloadLabel({
      shipmentId: parseInt(shipment.sendcloud_shipment_id),
    });

    if (!result.success || !result.data) {
      return null;
    }

    const labelData = result.data as {
      label_url?: string;
      label_data?: string;
      content_type?: string;
    };

    if (labelData.label_data) {
      // Handle base64 PDF data
      const binaryString = atob(labelData.label_data);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      return new Blob([bytes], {
        type: labelData.content_type || "application/pdf",
      });
    } else if (labelData.label_url) {
      // Fetch from URL
      const response = await fetch(labelData.label_url);
      if (!response.ok) {
        return null;
      }
      return await response.blob();
    }

    return null;
  } catch (error) {
    console.error(`Error fetching label for shipment ${shipment.id}:`, error);
    return null;
  }
}

/**
 * Combine multiple PDF labels into a single multi-page PDF
 */
export async function combineLabelsIntoPDF(
  shipments: SendcloudShipment[],
  onProgress?: (current: number, total: number) => void
): Promise<Blob | null> {
  try {
    const pdfDoc = await PDFDocument.create();
    let processedCount = 0;

    for (const shipment of shipments) {
      const labelBlob = await fetchLabelAsBlob(shipment);
      if (labelBlob) {
        try {
          const labelPdf = await PDFDocument.load(await labelBlob.arrayBuffer());
          const pages = await pdfDoc.copyPages(
            labelPdf,
            labelPdf.getPageIndices()
          );
          pages.forEach((page) => pdfDoc.addPage(page));
        } catch (error) {
          console.error(
            `Error merging page for shipment ${shipment.id}:`,
            error
          );
          // Continue with other labels even if one fails
        }
      }
      processedCount++;
      onProgress?.(processedCount, shipments.length);
    }

    if (pdfDoc.getPageCount() === 0) {
      return null;
    }

    const pdfBytes = await pdfDoc.save();
    // Convert Uint8Array to ArrayBuffer for Blob constructor
    // Create a new ArrayBuffer to avoid type issues
    const buffer = new ArrayBuffer(pdfBytes.length);
    const view = new Uint8Array(buffer);
    view.set(pdfBytes);
    return new Blob([buffer], { type: "application/pdf" });
  } catch (error) {
    console.error("Error combining PDFs:", error);
    return null;
  }
}

/**
 * Create a ZIP file containing all labels as separate PDF files
 */
export async function createLabelsZip(
  shipments: SendcloudShipment[],
  onProgress?: (current: number, total: number) => void
): Promise<Blob | null> {
  try {
    const zip = new JSZip();
    let processedCount = 0;

    for (const shipment of shipments) {
      const labelBlob = await fetchLabelAsBlob(shipment);
      if (labelBlob) {
        const filename = `label_${shipment.sendcloud_shipment_id || shipment.id}_${
          shipment.tracking_number || "no-tracking"
        }.pdf`;
        zip.file(filename, labelBlob);
      }
      processedCount++;
      onProgress?.(processedCount, shipments.length);
    }

    if (Object.keys(zip.files).length === 0) {
      return null;
    }

    const zipBlob = await zip.generateAsync({ type: "blob" });
    return zipBlob;
  } catch (error) {
    console.error("Error creating ZIP file:", error);
    return null;
  }
}

/**
 * Download a blob as a file
 */
export function downloadBlob(blob: Blob, filename: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => window.URL.revokeObjectURL(url), 1000);
}

